// AdminCertificatesPage — Generate official certificates for funded + reward events
// Mirrors customer CertificatesPage but with bulk generate

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface CertRequest {
  id: string;
  trader: string;
  avatar: number;
  account: string;
  tier: 'Beginner' | 'Mid' | 'Professional';
  accountSize: number;
  type: 'funded' | 'reward' | 'milestone';
  payoutNumber?: number;
  amount?: number;
  triggeredAt: string;
  status: 'pending' | 'generated' | 'sent' | 'error';
}

const REQUESTS: CertRequest[] = [
  { id: 'c1', trader: 'Marcus J.', avatar: 1, account: 'FP-100K-14283', tier: 'Mid',          accountSize: 100000, type: 'reward',    payoutNumber: 1, amount: 1240,  triggeredAt: '2026-07-15', status: 'sent' },
  { id: 'c2', trader: 'Marcus J.', avatar: 1, account: 'FP-100K-14283', tier: 'Mid',          accountSize: 100000, type: 'reward',    payoutNumber: 2, amount: 2180,  triggeredAt: '2026-07-20', status: 'sent' },
  { id: 'c3', trader: 'Marcus J.', avatar: 1, account: 'FP-100K-14283', tier: 'Mid',          accountSize: 100000, type: 'reward',    payoutNumber: 3, amount: 1890,  triggeredAt: '2026-07-23', status: 'sent' },
  { id: 'c4', trader: 'Sarah C.',  avatar: 2, account: 'FP-150K-12844', tier: 'Professional', accountSize: 150000, type: 'funded',                            triggeredAt: '2026-07-12', status: 'sent' },
  { id: 'c5', trader: 'Lisa H.',   avatar: 4, account: 'FP-50K-15034',  tier: 'Beginner',     accountSize: 50000,  type: 'milestone', amount: 5000,                        triggeredAt: '2026-07-23', status: 'generated' },
  { id: 'c6', trader: 'Felix W.',  avatar: 1, account: 'FP-50K-14672',  tier: 'Beginner',     accountSize: 50000,  type: 'reward',    payoutNumber: 1, amount: 480,   triggeredAt: '2026-07-24', status: 'pending' },
  { id: 'c7', trader: 'Brent O.',  avatar: 3, account: 'FP-100K-14821', tier: 'Mid',          accountSize: 100000, type: 'funded',                             triggeredAt: '2026-07-24', status: 'error' },
];

const TIER_COLOR = { Beginner: '#34C759', Mid: '#0A84FF', Professional: '#1C1C1E' };
const TYPE_META = {
  funded:    { icon: '🏆', color: '#34C759', label: 'Funded' },
  reward:    { icon: '💰', color: '#FF9500', label: 'Reward' },
  milestone: { icon: '🎯', color: '#5856D6', label: 'Milestone' },
};
const STATUS_META: Record<string, { color: string; bg: string; label: string }> = {
  pending:    { color: '#FF9500', bg: 'rgba(255,149,0,0.15)', label: 'Pending' },
  generated:  { color: '#0A84FF', bg: 'rgba(10,132,255,0.15)', label: 'Generated' },
  sent:       { color: '#34C759', bg: 'rgba(52,199,89,0.15)', label: 'Sent' },
  error:      { color: '#FF3B30', bg: 'rgba(255,59,48,0.15)', label: 'Error' },
};

export function AdminCertificatesPage() {
  const nav = useNavigate();
  const [requests, setRequests] = useState(REQUESTS);
  const [filter, setFilter] = useState<'all' | 'pending' | 'error' | 'sent'>('pending');

  const filtered = requests.filter((r) => filter === 'all' || r.status === filter);

  function setStatus(ids: string[], status: CertRequest['status']) {
    setRequests((curr) => curr.map((r) => ids.includes(r.id) ? { ...r, status } : r));
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-white/10 bg-[#0A0A0A]/80 backdrop-blur-xl px-2">
        <button onClick={() => nav('/admin/dashboard')} data-testid="back" className="flex h-9 w-9 items-center justify-center text-[#0A84FF] haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Certificates</div>
        <button onClick={() => alert('Manually triggered cert for all funded traders')} data-testid="bulk-gen" className="px-3 text-[15px] text-[#0A84FF]">+ Generate</button>
      </div>

      {/* Stats */}
      <div className="mx-4 mt-3 grid grid-cols-4 gap-2">
        <Stat label="Total"     value={requests.length} color="#0A84FF" />
        <Stat label="Pending"   value={requests.filter((r) => r.status === 'pending').length} color="#FF9500" />
        <Stat label="Sent"      value={requests.filter((r) => r.status === 'sent').length} color="#34C759" />
        <Stat label="Errors"    value={requests.filter((r) => r.status === 'error').length} color="#FF3B30" />
      </div>

      {/* Filter */}
      <div className="px-4 pt-3 flex items-center gap-1.5 overflow-x-auto">
        {(['pending', 'error', 'sent', 'all'] as const).map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`cfilter-${f}`}
            className={['shrink-0 rounded-full px-3 py-1.5 text-[12px] font-semibold capitalize transition',
              filter === f ? 'bg-white text-black' : 'bg-white/10 text-white/70 active:opacity-40'
            ].join(' ')}
          >{f}</button>
        ))}
      </div>

      {/* List */}
      <div className="px-4 mt-3 space-y-2">
        {filtered.map((r) => {
          const tm = TYPE_META[r.type];
          const sm = STATUS_META[r.status];
          return (
            <div key={r.id} data-testid={`cert-${r.id}`} className="rounded-[14px] border border-white/10 bg-white/5 p-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] text-[20px]" style={{ backgroundColor: tm.color + '20' }}>{tm.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[13px] font-bold text-white">{r.trader}</span>
                    <span className="rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase" style={{ backgroundColor: tm.color + '20', color: tm.color }}>{tm.label}</span>
                    <span className="rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase" style={{ backgroundColor: sm.bg, color: sm.color }}>{sm.label}</span>
                  </div>
                  <div className="text-[11px] text-white/50 font-mono">{r.account} · ${(r.accountSize/1000).toFixed(0)}K {r.tier}</div>
                  {r.amount && <div className="text-[11px] text-white/70 mt-0.5">${r.amount.toLocaleString()} {r.payoutNumber ? `· Payout #${r.payoutNumber}` : ''}</div>}
                </div>
                <div className="text-right text-[10px] text-white/40">{r.triggeredAt}</div>
              </div>
              {r.status === 'pending' && (
                <div className="mt-2 flex gap-2">
                  <button onClick={() => setStatus([r.id], 'generated')} data-testid={`gen-${r.id}`} className="flex-1 h-8 rounded-full bg-[#0A84FF] text-white text-[12px] font-bold active:opacity-40">Generate</button>
                  <button onClick={() => setStatus([r.id], 'sent')} data-testid={`send-${r.id}`} className="flex-1 h-8 rounded-full bg-[#34C759] text-black text-[12px] font-bold active:opacity-40">Send now</button>
                </div>
              )}
              {r.status === 'error' && (
                <div className="mt-2 rounded-[8px] bg-[#FF3B30]/10 border border-[#FF3B30]/30 p-2 text-[11px] text-[#FF3B30]">
                  ⚠ Send failed: KYC incomplete. <button onClick={() => alert('Email verification sent to trader')} data-testid={`resend-${r.id}`} className="underline">Resend verification →</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="rounded-[10px] bg-white/5 border border-white/10 p-2 text-center">
      <div className="text-[9px] text-white/60 uppercase">{label}</div>
      <div className="text-[16px] font-bold" style={{ color }}>{value}</div>
    </div>
  );
}
